import { Component, OnInit } from '@angular/core';

import { environment } from 'environments/environment';
import { datadogRum } from '@datadog/browser-rum';
import packageJson from '../../package.json';
import { Translate } from '@mottu-ops/locale';
import { LocalStorageService } from '@mottu-ops/global-components';
import { FeatureFlagService } from '@mottu-ops/feature-flag';
import { CheckForUpdateService } from '@core/services/check-for-update.service';
import { ITotem } from '@shared/interfaces';
import { initConfig } from '@shared/utils/feature-flag.utils';
import { setGlobalPropertiesDatadog } from '@shared/utils/observability.utils';
import { KeycloakEventType, KeycloakService } from '@mottu-ops/keycloak-angular';
import { MaintenanceOperationBffService } from '@shared/services/maintenance-operation-bff.service';
import { LoggedUserService } from '@shared/services/logged-user.service';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@mottu-ops/sso';

@Translate
@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
	featureFlagsLoaded = false;

	constructor(
		private checkUpdateService: CheckForUpdateService,
		private featureFlagService: FeatureFlagService,
		private localStorage: LocalStorageService,
		private keycloakService: KeycloakService,
		private maintenanceOperationBff: MaintenanceOperationBffService,
		private loggedUserService: LoggedUserService,
		private authService: AuthService
	) {}

	async ngOnInit() {
		try {
			const isLoggedIn = this.keycloakService.isLoggedIn();
			if (!isLoggedIn) {
				await this.keycloakService.login();
			} else {
				const token = await this.keycloakService.getToken();
				await this._setLoggedUserInfo(token);

				this.checkUpdateService.init();
				this.setViewPortSize();
				this.enableDataDog();
				await this.initFeatureFlagAndSetGlobalContext();

				this.keycloakService.keycloakEvents$.subscribe({
					next: async event => {
						if (event.type == KeycloakEventType.OnTokenExpired) {
							const token = await this._updateToken();
							await this._setLoggedUserInfo(token);
						}

						if (
							event.type == KeycloakEventType.OnAuthRefreshError ||
							event.type == KeycloakEventType.OnAuthError ||
							event.type == KeycloakEventType.OnAuthLogout
						) {
							await this.keycloakService.login();
						}
					},
				});
			}
		} catch (error) {
			await this.keycloakService.login();
		}
	}

	private async initFeatureFlagAndSetGlobalContext() {
		const totem = this.localStorage.getParseItem<ITotem>('mottu-totem.access');
		if (totem) {
			const config = initConfig(totem);
			await this.featureFlagService.initialize(config);
			setGlobalPropertiesDatadog(totem);
		}
		this.featureFlagsLoaded = true;
	}

	private enableDataDog(): void {
		if (!environment.enableObservability) return;

		datadogRum.init({
			applicationId: '9bdf5aa8-8adb-4401-9d54-fa9b3f4c77ac',
			clientToken: 'pub9b9e2aa0fda424126d30b8f1da34eb57',
			site: 'us5.datadoghq.com',
			service: 'totem-front',
			env: 'prod',
			version: packageJson.version,
			sessionSampleRate: 100,
			sessionReplaySampleRate: 40,
			trackUserInteractions: true,
			trackResources: true,
			trackLongTasks: true,
			defaultPrivacyLevel: 'allow',
			enableExperimentalFeatures: ['feature_flags'],
			allowedTracingUrls: [/[A-Za-z09]+\.mottu\.cloud/g],
		});
	}

	private setViewPortSize(): void {
		const w = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
		const h = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

		const body = document.querySelector('body');
		const html = document.querySelector('html');

		if (body)
			body.style.cssText = `
			width: ${w};
			height: ${h};
		`;

		if (html)
			html.style.cssText = `
			width: ${w};
			height: ${h};
		`;
	}

	private async _updateToken(): Promise<string> {
		await this.keycloakService.updateToken();

		return await this.keycloakService.getToken();
	}

	private async _setLoggedUserInfo(token: string): Promise<void> {
		this.authService.credentials = {
			...this.authService.credentials,
			accessToken: token,
		};

		const userInfo$ = this.maintenanceOperationBff.getLoggedUserInfo(token);
		const userInfo = await firstValueFrom(userInfo$);
		this.loggedUserService.setLoggedUserInfo(userInfo);
	}
}
